import { cx } from '@emotion/css';
import type { FC, ReactNode } from 'react';

import { MotifComponent, useMotifStyles } from '../../motif';
import type { ImageSources } from '../../types';
import { ImageButton } from '../ImageButton';
import { Media } from '../Media';
import {
  authorCss,
  authorWithoutLogoCss,
  imageButtonContainerCss,
  imageCss,
  longQuoteTextCss,
  quoteContainerCss,
  quoteCss,
  quoteIconCss,
  quoteMediaContainerCss,
  quoteTextCss,
  videoCss,
} from './QuoteV2.styled';

interface Props {
  author?: ReactNode;
  children?: ReactNode;
  logoImageSrc?: ImageSources;
  isRtl?: boolean;
  isLongQuote?: boolean;
  // image settings
  imgSrcs?: ImageSources;
  // video setings
  videoSrc?: string;
  mobileVideoSrc?: string;
}

export const QuoteV2: FC<Props> = ({
  author,
  children,
  logoImageSrc,
  isRtl = false,
  isLongQuote = false,
  imgSrcs,
  videoSrc,
  mobileVideoSrc,
}) => {
  useMotifStyles(MotifComponent.QUOTE);

  // Add extra spacing when there's no logo
  const authorStylesWithSpacing = !logoImageSrc ? authorWithoutLogoCss : authorCss;
  const quoteTextStyles = isLongQuote ? longQuoteTextCss : quoteTextCss;
  const quoteIcon = isRtl ? '”' : '“';

  const mediaCss = cx(videoSrc ? videoCss : imageCss);
  const hasMedia = !!(imgSrcs || videoSrc);

  return (
    <div className={quoteContainerCss}>
      {/* Only render media container when there's media and not a long quote */}
      {hasMedia && !isLongQuote && (
        <div className={quoteMediaContainerCss}>
          <Media
            showVideoControls={false}
            videoSource={videoSrc}
            mobileVideoSource={mobileVideoSrc}
            imgSrcs={imgSrcs}
            className={mediaCss}
            isDraggable={false}
          />
        </div>
      )}

      <div className={cx(MotifComponent.QUOTE, quoteCss)}>
        <div className={quoteIconCss}> {quoteIcon}</div>
        <div className={quoteTextStyles}>{children}</div>

        {logoImageSrc && (
          <div className={imageButtonContainerCss}>
            <ImageButton width={98} imgSrcs={logoImageSrc} altText="Quote background" />
          </div>
        )}

        {author && <div className={authorStylesWithSpacing}>{author}</div>}
      </div>
    </div>
  );
};

QuoteV2.displayName = 'QuoteV2';
